import { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import { Util } from 'utils/common.utils';
import { _noop } from 'utils/function.utils';
import { ButtonMetadata, ButtonSize, ButtonType } from './button-types';
import styles from './button.module.scss';

interface ButtonProps {
  meta?: ButtonMetadata;
  classes?: string[];
  onClick?: (e: MouseEvent) => void;
  onMouseEnter?: (e: MouseEvent) => void;
  onMouseLeave?: (e: MouseEvent) => void;
  children?: JSX.Element | string;
  reducedPadding?: boolean;
  analyticsComponentName?: string;
  analyticsAdditionalProps?: any;
  customAnalytics?: any;
  type?: 'submit' | 'reset' | 'button';
}

export const Button = observer(
  ({
    meta,
    classes,
    onClick,
    onMouseEnter,
    onMouseLeave,
    children,
    reducedPadding = false,
    analyticsComponentName = 'button',
    analyticsAdditionalProps,
    customAnalytics = null,
    type,
  }: ButtonProps) => {
    const navigate = useNavigate();

    const hasCustomAnalytics = !!customAnalytics;

    let overWriteKeys = hasCustomAnalytics ? {} : {
      buttonCta: meta?.analyticsTitle || meta?.title || analyticsComponentName,
      areaName: meta?.areaName || meta?.analyticsTitle || meta?.title || analyticsComponentName,
      currentUrl: Util.CurrentLocation(),
      targetUrl: meta?.analyticsTargetUrl,
      productName: meta?.productName,
      entryPoint: meta?.entryPoint,
      paymentDueAmount: meta?.paymentDueAmount
    };

    if (!hasCustomAnalytics && analyticsAdditionalProps) {
      overWriteKeys = { ...overWriteKeys, ...analyticsAdditionalProps };
    }

    const componentAnalytics = hasCustomAnalytics
      ? customAnalytics
      : AnalyticsStructureStore.getComponentAnalyticsAsJson(
        analyticsComponentName,
        overWriteKeys
      );

    const analyticsTags = (meta?: ButtonMetadata) => {
      return {
        'data-component-name': analyticsComponentName || meta?.areaName,
        'data-component-info': JSON.stringify(componentAnalytics),
      };
    };

    const handleClick = (e: any) => {
      if (meta?.disabled) {
        return;
      }

      if (onClick) {
        onClick(e);
      }

      if (!meta?.targetUrl) {
        return;
      }

      // TODO: Improve external link handling
      if (meta.targetUrl?.startsWith('http')) {
        window.open(meta?.targetUrl, meta?.target !== 'self' ? '_blank' : '_self');
        return;
      }

      navigate(meta.targetUrl);
    };

    return (
      <button
        className={[
          ...(classes || []),
          styles.btn,
          'btn',
          meta?.type ? styles[meta?.type] : styles[ButtonType.Primary],
          meta?.size ? styles[meta?.size] : styles[ButtonSize.Medium],
          reducedPadding && styles.reducedPadding,
          meta?.disabled && styles['btn-disabled'],
        ].join(' ')}
        onClick={handleClick}
        onMouseEnter={!meta?.disabled ? onMouseEnter : _noop}
        onMouseLeave={!meta?.disabled ? onMouseLeave : _noop}
        disabled={meta?.disabled ?? false}
        type={type}
        {...analyticsTags(meta)}
      >
        {children || meta?.title}
      </button>
    );
  }
);